import { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useCart } from '../../context/CartContext'
import { fromUTC, getUserLocalTimezone } from '../../utils/timezoneUtils'
import { isFormStylingApplied, isButtonStylingApplied } from '../../utils/sectionFormStyling'
import PaginationControls from './PaginationControls'

const ITEMS_PER_PAGE = 10

export default function PublicTicketsSection({ websiteSlug, website, section, onAddToCart, sectionFormStyling, useDefaultStyling = true, sectionType = 'tickets' }) {
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [addingToCart, setAddingToCart] = useState(null)
  const isFormStyling = useDefaultStyling || isFormStylingApplied(sectionFormStyling, sectionType, section?._id)
  const isButtonStyling = useDefaultStyling || isButtonStylingApplied(sectionFormStyling, sectionType, section?._id)
  
  // Default values to fallback to if styling is not provided
  const DEFAULT_FORM_COLORS = {
    backgroundColor: '#FFFFFF',
    textColor: '#111827',
    hoverBackgroundColor: '#F9FAFB',
    hoverTextColor: '#374151',
    buttonBackgroundColor: '#3B82F6',
    buttonTextColor: '#FFFFFF',
    hoverButtonBackgroundColor: '#2563eb',
    hoverButtonTextColor: '#ffffff'
  }
  
  // Ensure we have valid styling values
  const formColors = isFormStyling ? (sectionFormStyling || DEFAULT_FORM_COLORS) : undefined
  
  const formStyle = isFormStyling ? {
    backgroundColor: formColors?.backgroundColor || DEFAULT_FORM_COLORS.backgroundColor,
    color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor
  } : undefined
  const buttonStyle = isButtonStyling ? {
    backgroundColor: formColors?.buttonBackgroundColor || DEFAULT_FORM_COLORS.buttonBackgroundColor,
    color: formColors?.buttonTextColor || DEFAULT_FORM_COLORS.buttonTextColor
  } : { backgroundColor: '#3B82F6', color: '#FFFFFF' } // Always provide default button styling
  const textStyle = isFormStyling ? { color: formColors?.textColor || DEFAULT_FORM_COLORS.textColor } : undefined
  const [currentPage, setCurrentPage] = useState(1)
  const navigate = useNavigate()
  const { addToCart } = useCart()

  useEffect(() => {
    if (!websiteSlug && !website?.publicUrl) return

    fetchTickets()
    setCurrentPage(1)
  }, [websiteSlug, website?.publicUrl])

  const fetchTickets = async () => {
    try {
      setLoading(true)
      setError(null)
      
      let response;
      if (website?._id) {
        // For custom domains, use website ID
        response = await axios.get(`/event-tickets/website-public/${website._id}`)
      } else {
        // For regular domains, use slug
        const slug = website?.publicUrl || websiteSlug
        response = await axios.get(`/event-tickets/store/${slug}`)
      }
      
      if (!response.data?.tickets) {
        setTickets([])
        setError('No tickets available')
        return
      }
      setTickets(response.data.tickets)
    } catch (err) {
      console.error('[PublicTicketsSection] fetchTickets', err)
      setError(err.response?.data?.message || 'Unable to load tickets')
      setTickets([])
    } finally {
      setLoading(false)
    }
  }

  const handleBuyTicket = (ticket) => {
    if (!ticket) return

    setAddingToCart(ticket._id)

    const ticketItem = {
      _id: ticket._id,
      itemId: ticket._id,
      name: ticket.eventName || ticket.title || 'Ticket',
      description: ticket.eventDescription || ticket.shortDescription || '',
      price: Number(ticket.price) || 0,
      currency: ticket.currency || website?.paymentSettings?.currency || 'NGN',
      image: ticket.eventBanner || ticket.image || null,
      websiteId: website?._id || null,
      type: 'ticket',
      itemType: 'ticket' // Ensure API receives the correct ticket marker
    }

    addToCart(ticketItem)

    toast.success(`${ticketItem.name} added to cart`, { duration: 2000 })

    if (onAddToCart) {
      onAddToCart()
    }

    setTimeout(() => setAddingToCart(null), 300)
  }

  if (loading) return <div className="py-8 text-center">Loading tickets...</div>

  if (error) return <div className="py-8 text-center text-red-500">{error}</div>

  if (!tickets || tickets.length === 0) {
    return (
      <div className="py-8 text-center" style={textStyle}>No tickets currently available.</div>
    )
  }

  // Calculate pagination
  const totalPages = Math.ceil(tickets.length / ITEMS_PER_PAGE)
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE
  const endIdx = startIdx + ITEMS_PER_PAGE
  const paginatedTickets = tickets.slice(startIdx, endIdx)

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" style={formStyle}>
        {paginatedTickets.map((ticket) => {
        const eventDateUTC = ticket.eventDateUTC ? new Date(ticket.eventDateUTC) : null
        const timezone = ticket.timezone || 'America/Chicago'
        const userTimezone = getUserLocalTimezone()

        let eventDate = ticket.eventDate ? new Date(ticket.eventDate) : eventDateUTC || new Date()
        let startTime = ticket.startTime || ticket.startTimeUTC || '00:00'
        let endTime = ticket.endTime || ticket.endTimeUTC || '00:00'

        if (ticket.eventDateUTC && ticket.startTimeUTC) {
          try {
            const converted = fromUTC(eventDateUTC.toISOString().split('T')[0], ticket.startTimeUTC, timezone)
            eventDate = new Date(converted.iso)
            startTime = converted.time
          } catch (error) {
            console.error('[PublicTicketsSection] failed to convert eventDateUTC to timezone', error)
          }
        }

        if (ticket.eventDateUTC && ticket.endTimeUTC) {
          try {
            const convertedEnd = fromUTC(eventDateUTC.toISOString().split('T')[0], ticket.endTimeUTC, timezone)
            endTime = convertedEnd.time
          } catch (error) {
            console.error('[PublicTicketsSection] failed to convert endTimeUTC to timezone', error)
          }
        }

        const now = new Date()
        const start = ticket.salesStartDate ? new Date(ticket.salesStartDate) : null
        const end = ticket.salesEndDate ? new Date(ticket.salesEndDate) : null
        const saleWindowOpen = (!start || start <= now) && (!end || end >= now)

        const salesFlag = ticket.salesOpen !== false // default true if undefined

        const salesOpen =
          ticket.status === 'active' &&
          ticket.visibilityEnabled !== false &&
          salesFlag &&
          saleWindowOpen &&
          (ticket.soldQuantity || 0) < (ticket.totalQuantity || 0)

        const eventBannerUrl =
          ticket.eventBanner?.url ||
          (typeof ticket.eventBanner === 'string' ? ticket.eventBanner : null)

        return (
          <div key={ticket._id} className="border rounded-lg overflow-hidden shadow-sm bg-white">
            {eventBannerUrl ? (
              <img src={eventBannerUrl} alt={ticket.eventName} className="h-48 w-full object-cover" />
            ) : (
              <div className="h-48 w-full bg-gray-200 flex items-center justify-center">
                <span style={textStyle}>No Banner</span>
              </div>
            )}

            <div className="p-4">
              <h3 className="text-xl font-bold leading-tight">{ticket.eventName}</h3>
              <p className="text-sm" style={textStyle}>{ticket.eventCategory} • {ticket.ticketType}</p>
              <p className="mt-2 line-clamp-3" style={textStyle}>{ticket.eventDescription || ticket.shortDescription}</p>

              <div className="mt-3 text-sm" style={textStyle}>
                <div><strong>Date:</strong> {(() => {
                  if (ticket.eventDate) {
                    const [year, month, day] = ticket.eventDate.split('-');
                    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                    return `${monthNames[parseInt(month)-1]} ${parseInt(day)}, ${year}`;
                  }
                  return eventDate.toLocaleDateString();
                })()}</div>
                <div><strong>Time:</strong> {startTime} - {endTime} ({timezone})</div>
                <div><strong>Venue:</strong> {ticket.eventType === 'Online' ? 'Online' : ticket.venueName}</div>
                <div><strong>Price:</strong> {ticket.currency} {ticket.price}</div>
                <div className="text-xs" style={textStyle}>Local time: {ticket.eventDate} {startTime} ({ticket.timezone})</div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                  onClick={() => handleBuyTicket(ticket)}
                  style={buttonStyle}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-300 disabled:text-gray-500"
                  disabled={!salesOpen || addingToCart === ticket._id}
                >
                  {addingToCart === ticket._id ? 'Adding...' : salesOpen ? 'Buy Ticket' : 'Sales Closed'}
                </button>
                <span className="text-xs" style={textStyle}>Remaining: {ticket.totalQuantity - ticket.soldQuantity}</span>
              </div>
            </div>
          </div>
        )
        })}
      </div>

      {/* Show pagination if more than one page */}
      {totalPages > 1 && (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          loading={loading}
        />
      )}
    </>
  )
}
